import { requestContext } from 'client/utils/request-context/request-context';
import { IS_NODE } from 'client/utils/environment';

/**
 * @type {Record<string, boolean>}
 * @description An object to store default feature flag values. This is used in a Node.js environment when no request context is available.
 */
let defaultFeatureFlags = {};

export const featureFlags = {
  /**
   * Retrieves the value of a specific feature flag or all feature flags.
   *
   * In a browser environment, it attempts to retrieve feature flags from the request context.
   * In a Node.js environment, if no feature flags are found in the request context, it falls back to the default feature flags set using {@link FeatureFlags#setEnvironmentFeatureFlags}.
   *
   * @param {string} [ffName] - The name of the feature flag to retrieve. If not provided, all feature flags are returned.
   * @returns {boolean | Record<string, boolean> | undefined} The value of the specified feature flag, an object containing all feature flags, or undefined if the feature flag is not found.
   */
  get(ffName) {
    let featureFlagsObject = requestContext.get('featureFlags');

    if (IS_NODE && !featureFlagsObject) {
      featureFlagsObject = defaultFeatureFlags;
    }

    if (!ffName) return featureFlagsObject;

    return featureFlagsObject[ffName];
  },

  /**
   * Sets the default feature flags.
   *
   * This method is primarily used in a Node.js environment to provide fallback values when no request-specific feature flags are available.
   * Meant to be called by platform (i.e. during app initialization) to set feature flags
   * for given environment (i.e. dev, sandbox, qa, prod) as determined by config/*.yaml and
   * venom-admin configuration
   *
   * @param {Record<string, boolean>} flags - An object containing the default feature flags (key-value pairs).
   * @returns {void}
   */
  setEnvironmentFeatureFlags(flags) {
    defaultFeatureFlags = flags;
  },
};
